import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  TextField,
  Paper,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useNavigate } from "react-router-dom";
import PreviewAdvanceKyc from "../PreviewAdvanceKyc/PreviewAdvanceKyc";

import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import config from "../../../config/config";
import store from "../../../Store/Store";
import { useDebouncedLog } from "../../../useDebouncedLog";
import { verifyTokenAccess } from "../../../Helper";
import styles from "./advancedKyc.module.css";

const BASE_URL = "http://localhost:5050/api";

export default function AdvancedKyc({ companyId }) {
  const [data, setData] = useState("");
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);
  const [kycCompanyId, setKycCompanyId] = useState(companyId);
  const [form, setForm] = useState({
    name: "",
    designation: "",
    number: "",
    mail: "",
  });
  const [errors, setErrors] = useState({ name: "", mail: "", files: "" });

  // KYC + upload progress
  const [submitting, setSubmitting] = useState(false);
  const [providerResp, setProviderResp] = useState(null);
  const [providerErr, setProviderErr] = useState("");

  // NEW: files chosen but not yet uploaded
  const [selectedFiles, setSelectedFiles] = useState([]); // [{file, id}], not uploaded yet
  const [totalSize, setTotalSize] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // After upload
  const [uploadedDocs, setUploadedDocs] = useState([]); // [{document_id, filename, size}]
  const [uploadErr, setUploadErr] = useState("");

  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);
  const [replaceIndex, setReplaceIndex] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [pan, setPan] = useState("");
  const [entityType, setEntityType] = useState("");
  const navigate = useNavigate();
  const [companyTempId, setCompanyTempId] = useState("");
  const [FileSelected, setfileSelected] = useState(false);

  const DESIGNATION_OPTIONS = [
    "Director",
    "LLP Partner",
    "Authorized Signatory",
    "Proprietor",
    "Trustee",
    "Partner",
  ];

  const docsRequired =
    form.designation === "Authorized Signatory" ||
    form.designation === "Proprietor" ||
    form.designation === "Partner";

  var logAdvanceKycVisited;

  try {
    logAdvanceKycVisited = useDebouncedLog("admin_advance_kyc_visited", 500);
  } catch (err) {
    console.log("error", err);
  }

  useEffect(() => {
    setKycCompanyId(store.KycCompanyId ? store.KycCompanyId : companyId);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let tokens = localStorage.getItem("authToken");
        await verifyTokenAccess("ADV KYC FETCH DATA");
        let parsedTokens = JSON.parse(tokens);
        const res = await fetch(
          `${config.kyc_api_endpoint}/basic-kyc/by-company`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              aws_cognito_id_token: parsedTokens.IdToken,
              aws_cognito_access_token: parsedTokens.AccessToken,
            },
            body: JSON.stringify({
              company_id: kycCompanyId,
            }),
          },
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        setPan(data.data.PAN);
        setEntityType(data.data.Business_Entity_Type);
      } catch (error) {
        console.log("Error fetching KYC data:", error);
      }
    };

    if (kycCompanyId) {
      fetchData();
    }
  }, [kycCompanyId, config.kyc_api_endpoint]);

  useEffect(() => {
    setCompanyTempId(kycCompanyId);
  }, [store]);

  useEffect(() => {
    let abort = false;

    async function fetchKyc() {
      if (!companyTempId) {
        setErr("Missing company_id");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErr("");

        let tokens = localStorage.getItem("authToken");
        await verifyTokenAccess("ADV KYC FETCH KYC");
        let parsedTokens = JSON.parse(tokens);
        const res = await fetch(
          `${config.kyc_api_endpoint}/advanced-kyc/data`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              aws_cognito_id_token: parsedTokens.IdToken,
              aws_cognito_access_token: parsedTokens.AccessToken,
            },
            body: JSON.stringify({ company_id: companyTempId }),
          },
        );

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) {
          throw new Error(json?.message || `Request failed with ${res.status}`);
        }

        if (abort) return;

        const d = json?.data || {};
        setData(d);
        setStatus(d.status);
        // setStatus("pending_approval");
        if (
          d?.status === "rejected" ||
          d?.status === "requested" ||
          d?.status === "pending_approval"
        ) {
          setForm({
            name: d?.name || "",
            designation: d?.designation || "",
            number: d?.phone || "",
            mail: d?.email || "",
          });

          if (
            Array.isArray(d?.attached_documents) &&
            d.attached_documents.length > 0
          ) {
            setUploadedDocs(
              d.attached_documents.map((doc) => ({
                document_id: doc.document_id,
                filename: doc.filename,
                size: doc.size,
              })),
            );
          }
        }
      } catch (e) {
        if (!abort) setErr(e?.message || "Failed to load KYC data");
      } finally {
        if (!abort) setLoading(false);
      }
    }

    fetchKyc();
    return () => {
      abort = true;
    };
  }, [companyTempId, successOpen]);

  useEffect(() => {
    setSuccessOpen(!!providerResp);
  }, [providerResp]);

  const handleSuccessClose = () => {
    setSuccessOpen(false);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let cleaned = value;

    if (name === "number") {
      // Only digits, max 10
      cleaned = value.replace(/\D/g, "").slice(0, 10);
    } else if (name === "name") {
      // Only alphabets and spaces, strip numbers/special chars
      cleaned = value.replace(/[^a-zA-Z\s]/g, "");
    } else if (name === "mail") {
      cleaned = value.trimStart(); // no leading spaces
    }

    setForm((p) => ({ ...p, [name]: cleaned }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
    setProviderErr("");
    setProviderResp(null);
  };

  const formatBytes = (n) => {
    if (n === 0) return "0 B";
    const k = 1024;
    const dm = 1;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(n) / Math.log(k));
    return parseFloat((n / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  // ------- File choose / drag-drop (no upload yet) -------
  const openPicker = () => fileInputRef.current?.click();

  const validateAndStageFiles = (fileList, replaceAtIndex = null) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    // Only PDFs
    if (files.some((f) => f.type !== "application/pdf")) {
      setUploadErr("Only PDF files are allowed.");
      return;
    }

    // Enforce total size <= 10MB
    const incomingTotal = files.reduce((a, b) => a + b.size, 0);
    let newFiles = [];
    let newTotal = 0;

    if (replaceAtIndex !== null) {
      // replace a single file
      newFiles = [...selectedFiles];
      newTotal = totalSize - (selectedFiles[replaceAtIndex]?.file?.size || 0);
      newFiles[replaceAtIndex] = { file: files[0], id: crypto.randomUUID() };
      newTotal += files[0].size;
    } else {
      // append
      newFiles = [
        ...selectedFiles,
        ...files.map((f) => ({ file: f, id: crypto.randomUUID() })),
      ];
      newTotal = totalSize + incomingTotal;
    }

    if (newTotal > 10 * 1024 * 1024) {
      setUploadErr("Max total file size is 10MB.");
      return;
    }

    setSelectedFiles(newFiles);
    setfileSelected(true);
    setTotalSize(newTotal);
    setUploadErr("");
    setErrors((p) => ({ ...p, files: "" }));
  };

  const handleFileChange = (e) => {
    validateAndStageFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleReplaceChange = (e) => {
    validateAndStageFiles(e.target.files, replaceIndex);
    setReplaceIndex(null);
    if (replaceInputRef.current) replaceInputRef.current.value = "";
  };

  const removeFile = (id) => {
    const fileObj = selectedFiles.find((f) => f.id === id);
    if (!fileObj) return;
    const updated = selectedFiles.filter((f) => f.id !== id);
    setSelectedFiles(updated);
    setTotalSize((s) => s - (fileObj.file?.size || 0));
  };
  useEffect(() => {
    if (selectedFiles.length == 0 && form?.designation.trim() != "Director") {
      setfileSelected(false);
    } else {
      setfileSelected(true);
    }
  });
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    validateAndStageFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = () => setDragActive(false);

  // ------- Actual upload (called only on Proceed) -------
  async function uploadPdf(file) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${config.kyc_api_endpoint}/advanced-kyc/upload`, {
      method: "POST",
      body: fd,
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) {
      throw new Error(json?.message || "Upload failed");
    }
    return json; // { success, filename, size, ... }
  }

  const digitsOnly = (s = "") => s.replace(/[^\d]/g, "");

  const normalizeINMobile = (input = "") => {
    const d = digitsOnly(input);
    if (d.startsWith("91") && d.length === 12) return d.slice(2); // strip 91
    if (d.startsWith("0") && d.length === 11) return d.slice(1); // strip 0
    return d; // otherwise return as-is
  };

  const isValidINMobile = (input = "") => {
    const ten = normalizeINMobile(input);
    return /^[6-9]\d{9}$/.test(ten);
  };
  const handleProceedKyc = async () => {
    // basic form validation
    const nextErrors = { name: "", mail: "", files: "" };
    let valid = true;

    if (!form?.name?.trim()) {
      nextErrors.name = "Please enter your Name to proceed.";
      valid = false;
    } else if (form?.name?.trim().length < 3) {
      nextErrors.name = "Name must be at least 3 characters.";
      valid = false;
    } else if (!/^[A-Za-z\s]+$/.test(form?.name?.trim())) {
      nextErrors.name = "Name can only contain alphabets and spaces.";
      valid = false;
    }

    if (!form?.designation?.trim()) {
      nextErrors.designation = "Please select designation to proceed.";
      valid = false;
    }
    if (!form?.number?.trim()) {
      nextErrors.number = "Please enter your Mobile Number.";
      valid = false;
    } else if (!isValidINMobile(form?.number)) {
      nextErrors.number = "Enter a valid 10-digit Indian mobile number.";
      valid = false;
    }

    if (selectedFiles.length <= 0 && form?.designation.trim() != "Director") {
      console.log("NO FILE ADDED");
      nextErrors.name = "Please enter a File to proceed.";
      valid = false;
    }

    // if (docsRequired && selectedFiles.length === 0) {
    //   nextErrors.files = "Please add the required supporting document(s).";
    //   valid = false;
    // }
    console.log(nextErrors);

    setErrors(nextErrors);
    if (!valid) return;

    try {
      setSubmitting(true);
      setProviderErr("");
      setProviderResp(null);

      // 1) If there are files, upload them first
      let uploaded = [];

      const fd = new FormData();
      fd.append("company_id", kycCompanyId.trim());
      fd.append("name", form?.name.trim());
      fd.append("company_name", store.companyName);
      fd.append("pan", pan);
      // fd.append("entity_type", store.entity_type);
      fd.append("entity_type", entityType);
      fd.append("email", form?.mail.trim());
      fd.append("designation", form?.designation.trim());
      fd.append("ph_no", normalizeINMobile(form?.number));

      let tokens = localStorage.getItem("authToken");
      await verifyTokenAccess("ADV KYC PROCEED KYC");
      let parsedTokens = JSON.parse(tokens);
      selectedFiles.forEach(({ file }) => fd.append("documents", file));
      const res = await fetch(
        `${config.kyc_api_endpoint}/advanced-kyc/director/kyc_request`,
        {
          method: "POST",
          body: fd,
          headers: {
            aws_cognito_id_token: parsedTokens.IdToken,
            aws_cognito_access_token: parsedTokens.AccessToken,
          },
        },
      );

      const json = await res.json().catch(() => ({}));

      if (json?.success === true) {
        setSuccessOpen(true);
      }
      if (!res.ok || json?.success === false) {
        throw new Error(
          json?.error || json?.message || `Request failed with ${res.status}`,
        );
      }
      setProviderResp(json);
    } catch (err) {
      setProviderErr(
        err?.message || "Could not complete KYC. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const isPendingApproval =
    data?.status === "requested" || data?.status === "pending_approval";

  return (
    <>
      {status === "rejected" ? (
        <div
          role="alert"
          style={{
            width: "100%",
            backgroundColor: "#FFE5E2",
            border: "1px solid #F2B8B5",
            borderRadius: 12,
            padding: "16px 20px",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "center",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              maxWidth: 720, // keep it readable
              width: "100%",
              justifyContent: "center",
              textAlign: "center", // center text
              margin: "0 auto",
            }}
          >
            <div aria-hidden style={{ display: "flex", alignItems: "center" }}>
              <img src="/Dangerous.svg" alt="" />
            </div>

            <div>
              {" "}
              {/* no flex:1 here */}
              <h2
                style={{
                  margin: 0,
                  color: "#B20A00",
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                ADVANCED KYC REJECTED
              </h2>
              <div style={{ marginTop: 6, color: "#4C4E64", fontSize: 14 }}>
                <span style={{ fontWeight: 600 }}>Reason for rejection: </span>
                <span style={{ wordBreak: "break-word" }}>
                  {data?.rejectReason?.trim() || "Not provided"}
                </span>
              </div>
              <div style={{ paddingTop: "10px" }}>
                You may retry your Advanced KYC now.
              </div>
            </div>
          </div>
        </div>
      ) : data.status === "requested" || data?.status === "pending_approval" ? (
        <>
          <div
            role="alert"
            style={{
              width: "100%",
              backgroundColor: "#FFEED9",
              border: "1px solid #FFEED9",
              borderRadius: 12,
              padding: "20px 24px",
              boxSizing: "border-box",
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <div
              style={{
                maxWidth: 720,
                width: "100%",
                margin: "0 auto",
                textAlign: "left", // default left
              }}
            >
              {/* Title centered */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center", // ✅ centers horizontally
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <img
                  src="/pending.svg"
                  alt=""
                  style={{
                    /* width: 20, height: 20, */ display: "inline-block",
                  }}
                />
                {`ADVANCED KYC ${
                  status === "requested" ? "PENDING" : "PENDING APPROVAL"
                }`}
              </div>
            </div>
          </div>
          <div>
            <p
              style={{
                marginTop: 12,
                marginBottom: 12,
                color: "#1F2937",
                fontWeight: 600,
                fontFamily: "Sarabun",
              }}
            >
              The Digio Aadhaar &amp; PAN Verification KYC link has been sent to
              Phone number below:
            </p>

            {/* Name / Phone */}
            <div style={{ display: "grid", rowGap: 6, marginBottom: 16 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <span
                  style={{ minWidth: 90, color: "#4C4E64", fontWeight: 600 }}
                >
                  Name :
                </span>
                <span style={{ color: "#1E293B" }}>{form?.name}</span>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <span
                  style={{ minWidth: 90, color: "#4C4E64", fontWeight: 600 }}
                >
                  Phone no :
                </span>
                <span style={{ color: "#1E293B" }}>{form?.number}</span>
              </div>
            </div>

            {/* Steps */}
            <p
              style={{
                marginBottom: 8,
                color: "#1F2937",
                fontWeight: 600,
                fontFamily: "Sarabun",
              }}
            >
              To complete the Advanced KYC just follow these steps:
            </p>
            <ol
              style={{
                margin: 0,
                paddingLeft: 18,
                color: "#374151",
                lineHeight: 1.6,
                fontWeight: 400,
                fontFamily: "Sarabun",
              }}
            >
              <li>
                Open the e-Digio KYC link sent to your Mobile phone and click on{" "}
                <strong>'Send code to your mobile'</strong>
              </li>
              <li>Enter the security code you receive.</li>
              <li>
                Then click on Proceed to fetch your documents from{" "}
                <strong>Digilocker</strong>.
              </li>
              <li>
                Enter the Aadhaar number, followed by the OTP received on the
                registered phone number.
              </li>
              <li>
                Enter your 6 digit Digilocker security PIN to securely retrieve
                the documents.
              </li>
              <li>Provide your consent to share your documents with Digio.</li>
              <li>
                Next, take your selfie. (Make sure it matches the Aadhaar &amp;
                PAN documents.)
              </li>
              <li>Finally, click on Submit, and you are done!</li>
            </ol>

            {/* Footer note */}
            {status === "requested" && (
              <p
                style={{
                  marginTop: 14,
                  color: "#374151",
                  fontWeight: 600,
                  fontFamily: "Sarabun",
                  fontSize: "20px",
                }}
              >
                We will notify you, once we have verified the documents.
              </p>
            )}
            {/* Resend box */}
            {status === "requested" ? (
              <div
                style={{
                  marginTop: 16,
                  width: "40%",
                  gap: "20px",
                  padding: "12px 16px",
                  backgroundColor: "#F0F4FF",
                  border: "1px solid #F0F4FF",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 400,
                  fontFamily: "Sarabun",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ color: "#374151", fontSize: 20 }}>
                  Didn’t receive the KYC link?
                </span>
                <button
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#ffffff",
                    border: "1px solid #2563EB",
                    color: "#2563EB",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 500,
                    fontSize: 14,
                  }}
                  onClick={handleProceedKyc}
                >
                  Re-send Aadhar &amp; PAN Verification
                </button>
              </div>
            ) : status === "pending_approval" ? (
              <div
                style={{
                  marginTop: 16,
                  width: "30%",
                  gap: "20px",
                  padding: "12px 16px",
                  backgroundColor: "#F0F4FF",
                  border: "1px solid #F0F4FF",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 400,
                  fontFamily: "Sarabun",
                  justifyContent: "center",
                }}
              >
                <span style={{ color: "#374151", fontSize: 20 }}>
                  Your KYC details have been submitted successfully!
                </span>
              </div>
            ) : (
              ""
            )}
            {status === "pending_approval" && (
              <p
                style={{
                  marginTop: 14,
                  color: "#374151",
                  fontWeight: 600,
                  fontFamily: "Sarabun",
                  fontSize: "20px",
                }}
              >
                We will notify you, once we have verified the documents.
              </p>
            )}
          </div>
        </>
      ) : (
        ""
      )}

      {status === "approved" ? (
        <PreviewAdvanceKyc data={data} />
      ) : !status || status === "rejected" ? (
        <>
          {" "}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, mt: 2 ,color: "#4C4E64",fontFamily: "Sarabun", }}>
              Director/LLP/Partner/Proprietor/Trustee/Authorized Signatory details
            </Typography>
            <Box
              component="hr"
              sx={{ border: 0, borderTop: "1px solid #e5e7eb", mb: 2 }}
            />

            {/* Name */}
            <TextField
              fullWidth
              name="name"
              disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
              label={
                <>
                  Name (as per PAN)<span style={{ color: "red" }}> *</span>
                </>
              }
              value={form.name}
              onChange={onChange}
              error={!!errors.name}
              helperText={errors.name}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />

            {/* Designation + Phone */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ mt: 2 }}
            >
              <TextField
                fullWidth
                select // <-- makes it a dropdown
                name="designation"
                disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
                label={
                  <>
                    Designation<span style={{ color: "red" }}> *</span>
                  </>
                }
                value={form.designation || ""}
                onChange={onChange}
                variant="outlined"
                error={!!errors.designation}
                helperText={errors.designation}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              >
                <MenuItem value="" disabled>
                  Select designation
                </MenuItem>
                {DESIGNATION_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                name="number"
                disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
                label={
                  <>
                    Phone no<span style={{ color: "red" }}> *</span>
                  </>
                }
                value={form.number}
                onChange={onChange}
                variant="outlined"
                error={!!errors.number}
                helperText={errors.number}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
              />
            </Stack>

            {/* Email */}
            <TextField
              fullWidth
              sx={{
                mt: 2,
                "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              }}
              name="mail"
              label="Email ID"
              disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
              value={form.mail}
              onChange={onChange}
              variant="outlined"
            />

            {/* Upload docs card (stage only; no auto upload) */}
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 3, borderStyle: "dashed", borderRadius: "12px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, color: "#4C4E64", fontWeight: 700 }}
              >
                Upload Documents<span style={{ color: "red" }}> *</span>
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "#4C4E64" }}
                >
                  Note:
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 3,
                    listStyleType: "disc",
                    listStylePosition: "outside",
                    "& > li": { mb: 0.75, color: "#4C4E64" },
                  }}
                >
                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>Directors :</span> No
                    additional document required.
                  </Typography>

                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>LLP Partners :</span> LLP
                    Agreement.
                  </Typography>

                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>Authorized Signatory :</span>{" "}
                    Board Resolution.
                  </Typography>

                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>Proprietor :</span> Shop &amp;
                    Establishment Certificate / MSME Certificate / Trade License
                    <span className={styles.Bold}> (any two).</span>
                  </Typography>

                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>
                      Partner / Partnership Firm :
                    </span>{" "}
                    Partnership Deed.
                  </Typography>

                  <Typography component="li" variant="body2">
                    <span className={styles.Bold}>Trustee / Trust :</span> Trust
                    Deed / Trust Registration Certificate.
                  </Typography>
                </Box>
              </Box>

              {/* Info banner matching the image */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#E8F4FD",
                  border: "1px solid #B6D9F8",
                  borderRadius: "8px",
                  px: 2,
                  py: 1.25,
                  mb: 2,
                }}
              >
                <img
                  src="/Info-circle.svg"
                  alt="Info"
                  style={{ width: 20, height: 20, flexShrink: 0 }}
                />

                <Typography variant="body2" sx={{ color: "#0D47A1" }}>
                  Before proceeding for <span className={styles.Bold1}>Advanced KYC</span>, please attach all
                  mandatory documents.
                </Typography>
              </Box>

              {/* Hidden pickers */}
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                multiple
                disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <input
                ref={replaceInputRef}
                type="file"
                accept="application/pdf"
                disabled={store.isUserViewAdmin && !store.isUserGlobalViewAdmin}
                style={{ display: "none" }}
                onChange={handleReplaceChange}
              />

              {/* Drop area (staging only) */}
              <Box
                onClick={openPicker}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                sx={{
                  border: `1px dashed ${dragActive ? "#2348AB" : "#ccc"}`,
                  borderRadius: "8px",
                  p: 3,
                  cursor:
                    store.isUserViewAdmin && !store.isUserGlobalViewAdmin
                      ? "default"
                      : "pointer",
                  bgcolor: dragActive ? "#f2f6ff" : "#fafafa",
                  textAlign: "center",
                  transition: "all 0.15s ease",
                  opacity:
                    store.isUserViewAdmin && !store.isUserGlobalViewAdmin
                      ? 0.45
                      : 1,
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <img src="/FileUploadIcon.svg" alt="Upload file" />
                  <span>
                    Drag & drop the file here or{" "}
                    <Box
                      component="span"
                      sx={{ color: "#2348AB", textDecoration: "underline" }}
                    >
                     <span className={styles.Bold1}>Click to upload</span> 
                    </Box>
                  </span>
                </Typography>

                {uploadErr && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {uploadErr}
                  </Typography>
                )}
                {errors.files && (
                  <Typography
                    color="error"
                    variant="caption"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    {errors.files}
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  (Please upload only <span className={styles.Bold1}>PDF</span>. Maximum file size is 10MB.)
                </Typography>
              </Box>

              {/* Selected (staged) files list */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mt: 2, textAlign: "left" }}>
                  {selectedFiles.map(({ id, file }, idx) => (
                    <Stack
                      key={id}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        border: "1px solid #E3E3E3",
                        borderRadius: "8px",
                        p: 1,
                        mb: 1,
                        bgcolor: "#fff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                        }}
                      >
                        <img
                          src="/uploadImg.svg"
                          alt="Uploaded file"
                          style={{ width: 20, height: 20, flexShrink: 0 }}
                        />

                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "'Source Sans 3', sans-serif",
                            fontWeight: 400,
                            color: "#4C4E64",
                            size: 16,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                      <Box>
                        <div onClick={() => removeFile(id)}>
                          <img src="/removeFile.svg" />
                        </div>
                      </Box>
                    </Stack>
                  ))}
                  <Stack direction="row" gap={1} alignItems="center">
                    <Chip
                      size="small"
                      label={`Total: ${formatBytes(totalSize)}`}
                    />
                    <Button onClick={openPicker} size="small" variant="text">
                      Add more
                    </Button>
                  </Stack>
                </Box>
              )}
            </Paper>
          </Box>
          {/* Submit */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 5, justifyContent: "flex-end" }}
          >
            {!store.isUserViewAdmin && !store.isUserGlobalViewAdmin ? (
              <Button
                fullWidth={false}
                variant="contained"
                onClick={() => {
                  if (FileSelected) {
                    handleProceedKyc();
                  }
                }}
                // disabled={submitting || isPendingApproval}
                sx={
                  !FileSelected
                    ? { bgcolor: "#4764b4", cursor: "not-allowed" }
                    : { bgcolor: "#2348AB", "&:hover": { bgcolor: "#1b3890" } }
                }
                title={
                  isPendingApproval
                    ? "Your submission is under review"
                    : undefined
                }
              >
                {submitting
                  ? "Processing..."
                  : "Proceed to Aadhaar & PAN Verification"}
              </Button>
            ) : (
              <Button
                fullWidth={false}
                variant="contained"
                // disabled={submitting || isPendingApproval}
                sx={{
                  opacity: 0.45,
                  bgcolor: "#2348AB",
                  cursor: "default",
                }}
                title={
                  isPendingApproval
                    ? "Your submission is under review"
                    : undefined
                }
              >
                {submitting
                  ? "Processing..."
                  : "Proceed to Aadhaar & PAN Verification"}
              </Button>
            )}
          </Stack>
          {providerErr && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {providerErr}
            </Typography>
          )}
        </>
      ) : (
        ""
      )}
      {successOpen && (
        <Dialog
          open={successOpen}
          onClose={handleSuccessClose}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            e-KYC Link sent to your Phone Number.
            <IconButton
              aria-label="close"
              onClick={handleSuccessClose}
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
        </Dialog>
      )}
    </>
  );
}
